import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const feedbackSchema = z.object({
  generalFeedback: z.string()
    .trim()
    .min(10, "Please provide at least 10 characters")
    .max(1000, "Feedback must be less than 1000 characters"),
  
  desiredImageFrequency: z.enum([
    "1x per day",
    "2x per day",
    "4x per day",
    "6x per day",
    "12x per day",
    "24x per day"
  ], {
    errorMap: () => ({ message: "Please select a frequency" })
  }),
  
  desiredLocations: z.string()
    .trim()
    .min(5, "Please provide at least 5 characters")
    .max(500, "Locations must be less than 500 characters"),
  
  contactName: z.string()
    .trim()
    .max(100)
    .optional()
    .or(z.literal('')),
  
  contactEmail: z.string()
    .trim()
    .email("Invalid email format")
    .max(255)
    .optional()
    .or(z.literal('')),
  
  contactPhone: z.string()
    .trim()
    .max(20)
    .optional()
    .or(z.literal(''))
});

type FeedbackFormData = z.infer<typeof feedbackSchema>;

interface ShowcaseFeedbackFormProps {
  organizationId: string;
  organizationName: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function ShowcaseFeedbackForm({ 
  organizationId, 
  organizationName,
  onSuccess, 
  onCancel 
}: ShowcaseFeedbackFormProps) {
  const { register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue } = useForm<FeedbackFormData>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      generalFeedback: '',
      desiredImageFrequency: undefined,
      desiredLocations: '',
      contactName: '',
      contactEmail: '',
      contactPhone: ''
    }
  });

  const generalFeedback = watch('generalFeedback');
  const desiredLocations = watch('desiredLocations');
  const frequency = watch('desiredImageFrequency');

  const onSubmit = async (data: FeedbackFormData) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL || "https://ndqudoynysmynztrepwj.supabase.co"}/functions/v1/submit-showcase-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          organizationId,
          organizationName,
          generalFeedback: data.generalFeedback,
          desiredImageFrequency: data.desiredImageFrequency,
          desiredLocations: data.desiredLocations,
          contactName: data.contactName || undefined,
          contactEmail: data.contactEmail || undefined,
          contactPhone: data.contactPhone || undefined
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit feedback');
      }

      onSuccess();
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      throw error;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="showcase-feedback-form space-y-6">
      <div className="space-y-2">
        <Label htmlFor="generalFeedback">
          General Feedback <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="generalFeedback"
          placeholder="What do you think about Snapshot Devices?"
          className="min-h-[120px] resize-none"
          {...register('generalFeedback')}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{errors.generalFeedback?.message || ''}</span>
          <span>{generalFeedback?.length || 0}/1000</span>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="desiredImageFrequency">
          How often would you like to see new images? <span className="text-destructive">*</span>
        </Label>
        <Select
          value={frequency}
          onValueChange={(value) => setValue('desiredImageFrequency', value as any)}
        >
          <SelectTrigger id="desiredImageFrequency">
            <SelectValue placeholder="Select frequency..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1x per day">Once per day</SelectItem>
            <SelectItem value="2x per day">2 times per day</SelectItem>
            <SelectItem value="4x per day">4 times per day</SelectItem>
            <SelectItem value="6x per day">6 times per day</SelectItem>
            <SelectItem value="12x per day">12 times per day</SelectItem>
            <SelectItem value="24x per day">24 times per day (hourly)</SelectItem>
          </SelectContent>
        </Select>
        {errors.desiredImageFrequency && (
          <p className="text-xs text-destructive">{errors.desiredImageFrequency.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="desiredLocations">
          Which locations would you like to see cameras in? <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="desiredLocations"
          placeholder="e.g., City centers, parks, beaches, construction sites..."
          className="min-h-[80px] resize-none"
          {...register('desiredLocations')}
        />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>{errors.desiredLocations?.message || ''}</span>
          <span>{desiredLocations?.length || 0}/500</span>
        </div>
      </div>

      <div className="border-t pt-4 space-y-4">
        <p className="text-sm font-medium">Contact Details (Optional)</p>
        
        <div className="space-y-2">
          <Label htmlFor="contactName">Name</Label>
          <Input
            id="contactName"
            placeholder="Your name"
            {...register('contactName')}
          />
          {errors.contactName && (
            <p className="text-xs text-destructive">{errors.contactName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="contactEmail">Email</Label>
          <Input
            id="contactEmail"
            type="email"
            placeholder="your.email@example.com"
            {...register('contactEmail')}
          />
          {errors.contactEmail && (
            <p className="text-xs text-destructive">{errors.contactEmail.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="contactPhone">Phone</Label>
          <Input
            id="contactPhone"
            type="tel"
            placeholder="+44 123 456 7890"
            {...register('contactPhone')}
          />
          {errors.contactPhone && (
            <p className="text-xs text-destructive">{errors.contactPhone.message}</p>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          We collect anonymous usage data (IP hash) to prevent spam. Contact details are optional and only used to follow up on your feedback.
        </p>
      </div>

      <div className="flex gap-3 pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          className="flex-1"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Feedback'
          )}
        </Button>
      </div>
    </form>
  );
}
